// `common/directives/sort-indicator/sort-indicator.directive.ng1.ts`.
//
// The directive builds its template as a STRING at compile time, so the markup below is the
// string it produces rather than a reinterpretation of it.
import type { ReactElement } from 'react'

export interface SortOrder {
  field: string
  reverse: boolean
}

export interface SortIndicatorProps {
  /** The field this control sorts by — compared against `order.field` to pick the icon. */
  field: string
  order: SortOrder
  onSort: (field: string) => void
  /**
   * Upstream adds `pull-right` unless the `disable-pull-right` ATTRIBUTE is present — its
   * presence, not its value, so `disable-pull-right="false"` also disables it. Every call
   * site in the usage report passes it, so the default here is the one they all use.
   */
  pullRight?: boolean
}

/**
 * ⚠️ THE ICON IS A THREE-WAY, NOT A TOGGLE. `fa-sort` when this column is not the sorted one,
 * and only then `fa-sort-asc` / `fa-sort-desc`. Rendering asc/desc on an unsorted column
 * shows every header claiming to be sorted ascending.
 *
 * ⚠️ AND `fa-sort-DESC` IS THE NOT-REVERSED ONE. Upstream reads
 * `reverse ? 'fa-sort-asc' : 'fa-sort-desc'`, which looks backwards and is what ships, so the
 * arrow is transcribed rather than corrected.
 */
export function SortIndicator({ field, order, onSort, pullRight = false }: SortIndicatorProps): ReactElement {
  const icon = order.field !== field ? 'fa-sort' : order.reverse ? 'fa-sort-asc' : 'fa-sort-desc'
  return (
    <a
      className={pullRight ? 'pull-right sort-indicator' : 'sort-indicator'}
      // `href` with no value, exactly as the directive emits it: it is what makes the anchor
      // focusable and cursor-pointered without navigating.
      href=""
      onClick={(event) => {
        event.preventDefault()
        onSort(field)
      }}
    >
      <i className={`fa ${icon}`} />
    </a>
  )
}

/**
 * The directive's own `setSortField`: the same field flips direction, a different field
 * selects it ascending. Separate from the component because the ORDER lives in the caller —
 * one object shared by every column, which is how the headers stay mutually exclusive.
 */
export function nextSortOrder(order: SortOrder, field: string): SortOrder {
  if (order.field === field) return { field, reverse: !order.reverse }
  return { field, reverse: false }
}
